import { ITEM_GALLERY_BORDER, ITEM_THUMBNAIL_PX } from "@/components/ui/item-image-box-sizes";
import { ItemEmptyImageBox, ItemImageBox } from "@/components/ui/item-image-box";
import { itemImageSources, type CatalogItemImageInput } from "@/lib/catalog-item-image";
import { useItemImageFailure } from "@/hooks/use-item-image-failure";
import { cn } from "@/lib/utils";

// The donor's thumbnail strip — `ImagePreview.tsx:55-70`: a wrapping flex row of
// 68px squares, one per image, each of which swaps its picture into the main
// slot.
//
// **One component, because there are two strips.** The details page draws one
// under the gallery and the lightbox draws one under its picture, and they are
// the same strip — "see all the images" has to survive the step up in size. Two
// hand-kept copies is exactly how that stops being true; the first review of
// this slice found them already drifting apart.
//
// **The cells are keyboard-reachable, which is an owner-ruled parity break**
// (#303, D5). The donor's cell is a plain `Box` holding a `CardMedia
// component='img'` that carries the `onClick` (`:57-67`) — no button, no
// `tabIndex`, and the strip sits outside the `CardActionArea` (`:54-70`) — so
// tabbing modsy's details page goes straight past the thumbnails and the images
// can only be changed with a mouse. Here each cell is a real `<button>` with a
// focus ring: six tab stops and six rings the source has not got, ruled together
// with D2 on the grounds that a gallery whose later images are mouse-only is a
// genuine barrier. Ship it as a break, not as an accident.
//
// And one thing it deliberately does not do: **no active/selected marker.** The donor has
// none, and neither strip adds one — in the lightbox the picture is directly
// above the strip and the dialog's own title names the position, so a marker
// would be a third spelling of something already said twice.
//
// No ripple and no hover overlay either: the donor's strip sits *outside* the
// `CardActionArea` (`:54-70`), so that feedback belongs to the main slot alone.

interface ItemThumbnailStripProps {
  /** Every image, in the contract's order — including the one currently large. */
  images: readonly CatalogItemImageInput[];
  /** Show this image in the main slot. */
  onSelect: (index: number) => void;
  /** Whether the item is approved — the placeholder's pending variant. */
  isApproved?: boolean;
  /** The row's own layout — the caller's, since the two strips sit differently. */
  className?: string;
  "data-testid"?: string;
}

export function ItemThumbnailStrip({
  images,
  onSelect,
  isApproved = true,
  className,
  "data-testid": testId,
}: ItemThumbnailStripProps) {
  return (
    // `ImagePreview.tsx:56` — a wrapping flex row with no gap of its own; the
    // 12px margins on each cell are what space it.
    <div data-testid={testId} className={cn("flex flex-wrap", className)}>
      {images.map((image, position) => (
        <ItemThumbnailCell
          key={image.id}
          image={image}
          position={position}
          onSelect={onSelect}
          isApproved={isApproved}
        />
      ))}
    </div>
  );
}

/**
 * One 68px square — the donor's cell (`ImagePreview.tsx:57-67`).
 *
 * Its own component so it can hold **its own** fetch-failure verdict: a cell
 * resolves the ladder against 68px where the main slot resolves it against half
 * the page, so the two are fetching different rungs and cannot share an answer
 * (`useItemImageFailure`).
 *
 * **A failed cell keeps its place and its number.** The donor has no `onError`
 * and so never renumbers; dropping a dead image would shift every later cell's
 * `Thumbnail {n}` down one and change the count the lightbox names. What the
 * spec adds is the placeholder and nothing else (#303 AC 2, #269's "the no-image
 * and fetch-error fallback") — and a modsy cell is not showing a broken glyph in
 * its place, but a placeholder its image host served as the 403 body (#496).
 */
function ItemThumbnailCell({
  image,
  position,
  onSelect,
  isApproved,
}: {
  image: CatalogItemImageInput;
  position: number;
  onSelect: (index: number) => void;
  isApproved: boolean;
}) {
  const sources = itemImageSources(image);
  const [failed, noteFailed] = useItemImageFailure(sources);
  // `ImagePreview.tsx:63` — 1-based, and its own phrasing.
  const label = `Thumbnail ${position + 1}`;

  return (
    <button
      type="button"
      data-testid="gallery-thumbnail"
      onClick={() => onSelect(position)}
      // `ImagePreview.tsx:58` — `margin: 0 12px 12px 0`. `flex` rather than a
      // button's default inline layout: an inline-level cell puts the box on a
      // text baseline, and the line-height under it measured 72px against the
      // donor's 68.
      className={cn(
        "mr-3 mb-3 flex cursor-pointer rounded-[4px]",
        // The ring the donor has no need for, because its cell never takes
        // focus — part of the D5 break, not decoration.
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
      )}
    >
      {failed ? (
        <ItemEmptyImageBox
          isPendingApproval={!isApproved}
          // **The cell's number, not the placeholder's literal.** This box is
          // standing in for thumbnail 2; it has not stopped being thumbnail 2.
          // In the lightbox the cells are real controls, so the literal would
          // leave every failure in the strip named the same thing — and the
          // number is the only thing that ever told them apart.
          alt={label}
          boxPx={ITEM_THUMBNAIL_PX}
          className={ITEM_GALLERY_BORDER}
        />
      ) : (
        <ItemImageBox
          src={sources.src}
          sources={sources.sources}
          alt={label}
          boxPx={ITEM_THUMBNAIL_PX}
          className={ITEM_GALLERY_BORDER}
          // Above the fold, and the donor sets no hint at all
          // (`ImagePreview.tsx:59-66`); #302's lazy default is scoped by its own
          // reasoning to the card grids.
          loading="eager"
          onError={noteFailed}
        />
      )}
    </button>
  );
}
